import React, {
    useEffect, useState, useCallback, useRef,
} from 'react';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';
import { NOT_COMMISSARY_LABEL } from '../utils/loyalty';
import MemberSearchField, { memberDisplayName } from '../Components/MemberSearchField';
import { ReceiptPreviewModal, ReceiptThumbnail, thumbnailAriaLabel } from '../Components/ReceiptPreview';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Receipts emailed to scan@ from an address with no active account
// (somo-circle1-api#691). The loyalty-center Lambda holds them instead of
// dropping them, and the sender gets a sign-up invite. A held receipt is
// claimed automatically when the sender verifies an account at that
// address; this screen is for the rest: attach one to the right member by
// hand (a spouse's or work address), or discard one that should never be
// claimed. Neither action books points — an assigned receipt becomes an
// ordinary 'submitted' receipt in the Loyalty Receipts queue.

// Mirrors the `status` enum on unclaimedLoyaltyReceipt.model.js.
const STATUS_TABS = [
    { value: 'unclaimed', label: 'Unclaimed' },
    { value: 'claimed', label: 'Claimed' },
    { value: 'discarded', label: 'Discarded' },
    { value: 'all', label: 'All' },
];

const STATUS_META = {
    unclaimed: { badge: 'badge-warning', label: 'Unclaimed' },
    claimed: { badge: 'badge-success', label: 'Claimed' },
    discarded: { badge: 'badge-secondary', label: 'Discarded' },
};

const CLAIMED_VIA_LABEL = {
    registration: 'the sender signed up',
    login: 'the sender signed in',
    admin: 'assigned by an admin',
};

// Same labels as the review queue (LoyaltyReceipts.js) for the OCR outcomes
// that don't come with a usable total.
const OCR_STATUS_META = {
    failed: { badge: 'badge-danger', label: 'OCR failed' },
    unsupported: { badge: 'badge-secondary', label: 'OCR unsupported' },
    conversion_failed: { badge: 'badge-danger', label: 'Conversion failed' },
    pending: { badge: 'badge-secondary', label: 'OCR pending' },
    none: { badge: 'badge-light', label: 'No OCR data' },
};
const OCR_HAS_USABLE_TOTAL = ['completed', 'not_commissary'];

const PAGE_SIZE = 10;

const formatDollars = (amount) => `$${Number(amount).toFixed(2)}`;
const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '');

// populate()'d { name, lastName, email }, or null.
const personLabel = (person) => {
    if (!person) return null;
    return [person.name, person.lastName].filter(Boolean).join(' ').trim() || person.email || null;
};

// The From header carries the display name ("Julia Hefner <jh@...>"), which
// is the best clue to who sent it. Shown only when it adds something beyond
// the bare address. The sender sets it freely, so it's a hint, not identity.
const senderDisplayName = (r) => {
    const name = (r.fromHeader || '').replace(/<[^>]*>/g, '').replace(/"/g, '').trim();
    return name && name.toLowerCase() !== (r.senderEmail || '').toLowerCase() ? name : null;
};

function UnclaimedLoyaltyReceipts() {
    const [receipts, setReceipts] = useState([]);
    const [counts, setCounts] = useState({ unclaimed: 0, claimed: 0, discarded: 0 });
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('unclaimed');
    const [emailQuery, setEmailQuery] = useState('');
    const [preview, setPreview] = useState(null);

    // { receipt, mode: 'assign' | 'discard' } while a modal is open.
    const [action, setAction] = useState(null);
    const [assignMember, setAssignMember] = useState(null);
    const [discardReason, setDiscardReason] = useState('');
    const [actionError, setActionError] = useState('');
    const [actionInProgress, setActionInProgress] = useState(false);

    // Filters last sent to the server. Tabs, paging and the post-action
    // refresh use these, so a typed but unsubmitted email search never
    // applies by accident (same approach as LoyaltyReceipts.js).
    const appliedFiltersRef = useRef({ status: 'unclaimed', email: '' });
    // Only the latest list request may update state, so a slow response for
    // a tab the admin has already left can't overwrite the current one.
    const latestRequestRef = useRef(0);

    const fetchReceipts = useCallback(async (targetPage = 1, filters = {}) => {
        const { status: fStatus = 'unclaimed', email: fEmail = '' } = filters;
        appliedFiltersRef.current = { status: fStatus, email: fEmail };
        const requestId = ++latestRequestRef.current;
        const isStale = () => requestId !== latestRequestRef.current;
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: targetPage,
                limit: PAGE_SIZE,
                status: fStatus,
            });
            if (fEmail.trim()) queryParams.set('email', fEmail.trim());
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/unclaimed-receipts?${queryParams}`,
            );
            const data = await res.json().catch(() => ({}));
            if (isStale()) return;
            if (data.status === 'success') {
                setReceipts(data.data.receipts || []);
                setCounts({ unclaimed: 0, claimed: 0, discarded: 0, ...data.data.counts });
                setPagination(data.data.pagination || {});
                setPage(targetPage);
            } else {
                toast.error(data.message || 'Failed to load unclaimed receipts.');
            }
        } catch (err) {
            if (isStale()) return;
            console.error('Error fetching unclaimed receipts:', err);
            toast.error('Error loading unclaimed receipts.');
        } finally {
            if (!isStale()) setLoading(false);
        }
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        fetchReceipts(1, { status: 'unclaimed' });
    }, [fetchReceipts]);

    const handleTabChange = (next) => {
        setStatus(next);
        fetchReceipts(1, { ...appliedFiltersRef.current, status: next });
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        fetchReceipts(1, { status, email: emailQuery });
    };

    const handleResetFilters = () => {
        setStatus('unclaimed');
        setEmailQuery('');
        fetchReceipts(1, { status: 'unclaimed' });
    };

    const openAction = (receipt, mode) => {
        setAction({ receipt, mode });
        setAssignMember(null);
        setDiscardReason('');
        setActionError('');
    };

    const closeAction = () => {
        if (actionInProgress) return;
        setAction(null);
    };

    // On the Unclaimed tab the actioned receipt drops out of the list, so the
    // current page may now be past the end: go back to page 1. On the other
    // tabs it stays where it was, so stay on the current page.
    const refreshAfterAction = () => fetchReceipts(
        appliedFiltersRef.current.status === 'unclaimed' ? 1 : page,
        appliedFiltersRef.current,
    );

    const submitAction = async () => {
        const { receipt, mode } = action;
        const body = mode === 'assign'
            ? { userId: assignMember?._id }
            : { reason: discardReason.trim() };
        setActionError('');
        setActionInProgress(true);
        try {
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/unclaimed-receipts/${receipt._id}/${mode}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                },
            );
            // A gateway error page (HTML 502/504) isn't JSON; fall through to
            // the generic message rather than showing a parser error.
            const data = await res.json().catch(() => ({}));
            if (res.status === 409) {
                // Claimed by the sender (or another admin) since this page
                // loaded. Nothing to retry — show it and refresh the list.
                toast.error(data.message || 'This receipt is no longer unclaimed.');
                setAction(null);
                refreshAfterAction();
                return;
            }
            if (!res.ok || data.status !== 'success') {
                setActionError(data.message || 'That action failed. Please try again.');
                return;
            }
            toast.success(mode === 'assign'
                ? `Assigned to ${memberDisplayName(assignMember)} and submitted for review.`
                : 'Receipt discarded.');
            setAction(null);
            refreshAfterAction();
        } catch (err) {
            setActionError(err.message || 'That action failed. Please try again.');
        } finally {
            setActionInProgress(false);
        }
    };

    const canSubmitAction = action && !actionInProgress && (action.mode === 'assign'
        ? Boolean(assignMember)
        : discardReason.trim().length > 0);

    const allCount = counts.unclaimed + counts.claimed + counts.discarded;

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2 align-items-center">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Unclaimed Receipts</h1>
                        </div>
                        <div className="col-sm-6">
                            <ol className="breadcrumb justify-content-end">
                                <li className="breadcrumb-item">
                                    <Link to="/loyaltyReceipts"><i className="fa-solid fa-arrow-left-long" /> Back to Receipts</Link>
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <p className="text-muted">
                        Receipts emailed to scan@ from an address with no CircleOne account. The sender was invited
                        to sign up, and the receipt moves to the review queue on its own when they do. Assign one
                        to a member if you know who sent it, or discard it if it isn&apos;t a receipt. Neither
                        action books points.
                    </p>

                    <div className="btn-group mb-3 flex-wrap" role="group" aria-label="Filter by status">
                        {STATUS_TABS.map((tab) => (
                            <button
                                key={tab.value}
                                type="button"
                                className={`btn ${status === tab.value ? 'btn-blue' : 'btn-outline-secondary'}`}
                                aria-pressed={status === tab.value}
                                onClick={() => handleTabChange(tab.value)}
                            >
                                {tab.label}
                                {' '}
                                <span className="badge badge-light">
                                    {tab.value === 'all' ? allCount : counts[tab.value]}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="card mb-3">
                        <form className="card-body" onSubmit={handleSearchSubmit}>
                            <div className="row align-items-end">
                                <div className="col-md-4">
                                    <label htmlFor="unclaimed-email-filter">Sender email</label>
                                    <input
                                        id="unclaimed-email-filter"
                                        type="text"
                                        className="form-control"
                                        value={emailQuery}
                                        onChange={(e) => setEmailQuery(e.target.value)}
                                        placeholder="Search by sender email"
                                    />
                                </div>
                                <div className="col-md-3 d-flex" style={{ gap: '8px' }}>
                                    <button
                                        type="submit"
                                        className="btn btn-primary flex-fill"
                                        style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                    >
                                        Search
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary flex-fill"
                                        onClick={handleResetFilters}
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div className="table-responsive">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : receipts.length > 0 ? (
                            <>
                                <table width="100%" className="table table-striped table-bordered txt_lft">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '70px' }}>Receipt</th>
                                            <th style={{ width: '200px' }}>Sender</th>
                                            <th style={{ width: '130px' }}>OCR</th>
                                            <th style={{ width: '150px' }}>Received</th>
                                            <th style={{ width: '110px' }}>Status</th>
                                            <th style={{ width: '240px' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {receipts.map((r) => (
                                            <tr key={r._id}>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn p-0 border-0 bg-transparent"
                                                        aria-label={thumbnailAriaLabel(r.fileUrl)}
                                                        onClick={() => setPreview({ url: r.fileUrl, label: r.fileName || 'Receipt' })}
                                                    >
                                                        <ReceiptThumbnail fileUrl={r.fileUrl} />
                                                    </button>
                                                </td>
                                                <td>
                                                    {senderDisplayName(r) && (
                                                        <>
                                                            <span className="fwt_600">{senderDisplayName(r)}</span>
                                                            <br />
                                                        </>
                                                    )}
                                                    <span className="f_sz13">{r.senderEmail}</span>
                                                    {r.subject && (
                                                        <>
                                                            <br />
                                                            <small className="text-muted">Subject: {r.subject}</small>
                                                        </>
                                                    )}
                                                    {r.possibleDuplicate && (
                                                        <>
                                                            <br />
                                                            <small className="text-danger">
                                                                ⚠ Possible duplicate of
                                                                {r.duplicateOfUnclaimedId ? ' another held receipt' : ' a receipt already in the queue'}
                                                            </small>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    {OCR_HAS_USABLE_TOTAL.includes(r.ocrStatus) && Number.isFinite(r.grossTotal) ? (
                                                        <>
                                                            <span className={`badge ${r.ocrStatus === 'not_commissary' ? 'badge-warning' : 'badge-success'}`}>
                                                                {formatDollars(r.grossTotal)}
                                                            </span>
                                                            {r.ocrStatus === 'not_commissary' && (
                                                                <>
                                                                    <br />
                                                                    <small className="text-warning">{NOT_COMMISSARY_LABEL}</small>
                                                                </>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <span className={`badge ${(OCR_STATUS_META[r.ocrStatus] || OCR_STATUS_META.none).badge}`}>
                                                            {(OCR_STATUS_META[r.ocrStatus] || OCR_STATUS_META.none).label}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="f_sz13">
                                                    {formatDateTime(r.createdAt)}
                                                    <br />
                                                    <small className="text-muted">
                                                        {r.replySentAt ? `Invite sent ${formatDateTime(r.replySentAt)}` : 'No invite sent'}
                                                    </small>
                                                </td>
                                                <td>
                                                    <span className={`badge ${(STATUS_META[r.status] || {}).badge || 'badge-secondary'}`}>
                                                        {(STATUS_META[r.status] || {}).label || r.status}
                                                    </span>
                                                </td>
                                                <td>
                                                    {r.status === 'unclaimed' && (
                                                        <div className="d-flex flex-wrap" style={{ gap: '6px' }}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary"
                                                                style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                                                onClick={() => openAction(r, 'assign')}
                                                            >
                                                                Assign to member
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => openAction(r, 'discard')}
                                                            >
                                                                Discard
                                                            </button>
                                                        </div>
                                                    )}
                                                    {r.status === 'claimed' && (
                                                        <small className="text-muted">
                                                            Claimed by {personLabel(r.claimedUserId) || 'a member'}
                                                            {r.claimedVia ? ` — ${CLAIMED_VIA_LABEL[r.claimedVia] || r.claimedVia}` : ''}
                                                            {r.claimedVia === 'admin' && personLabel(r.claimedBy) ? ` (${personLabel(r.claimedBy)})` : ''}
                                                            {r.claimedAt ? ` on ${formatDateTime(r.claimedAt)}` : ''}
                                                        </small>
                                                    )}
                                                    {r.status === 'discarded' && (
                                                        <small className="text-muted">
                                                            Discarded
                                                            {personLabel(r.discardedBy) ? ` by ${personLabel(r.discardedBy)}` : ''}
                                                            {r.discardedAt ? ` on ${formatDateTime(r.discardedAt)}` : ''}
                                                            {r.discardReason && (
                                                                <>
                                                                    <br />
                                                                    Reason: {r.discardReason}
                                                                </>
                                                            )}
                                                        </small>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {pagination.totalPages > 1 && (
                                    <nav className="mt-3 d-flex flex-column align-items-center">
                                        <ul className="pagination justify-content-center mb-1">
                                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    onClick={() => fetchReceipts(page - 1, appliedFiltersRef.current)}
                                                    disabled={page === 1}
                                                >
                                                    Previous
                                                </button>
                                            </li>
                                            <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                                                <button
                                                    type="button"
                                                    className="page-link"
                                                    onClick={() => fetchReceipts(page + 1, appliedFiltersRef.current)}
                                                    disabled={page === pagination.totalPages}
                                                >
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                        <small className="text-muted">
                                            Page {pagination.currentPage} of {pagination.totalPages}
                                            {Number.isFinite(pagination.totalItems) ? ` · ${pagination.totalItems} receipts` : ''}
                                        </small>
                                    </nav>
                                )}
                            </>
                        ) : (
                            <div className="text-center p-5">
                                <p>Nothing here right now.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ReceiptPreviewModal preview={preview} onClose={() => setPreview(null)} />

            {/* Assign / discard — lower z-index than the receipt preview
                (1055 vs 1060) so the receipt can still be opened full-size
                from inside this modal. */}
            {action && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 1055 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content" data-testid="unclaimed-action-modal">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {action.mode === 'assign' ? 'Assign receipt to a member' : 'Discard receipt'}
                                </h5>
                                {!actionInProgress && (
                                    <button type="button" className="close" onClick={closeAction}>
                                        <span>&times;</span>
                                    </button>
                                )}
                            </div>
                            <div className="modal-body">
                                <p className="mb-3">
                                    Sent by <strong>{action.receipt.senderEmail}</strong> on {formatDateTime(action.receipt.createdAt)}.
                                    {' '}
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 border-0 align-baseline"
                                        onClick={() => setPreview({ url: action.receipt.fileUrl, label: action.receipt.fileName || 'Receipt' })}
                                    >
                                        View receipt
                                    </button>
                                </p>
                                {action.mode === 'assign' ? (
                                    <>
                                        {senderDisplayName(action.receipt) && (
                                            <small className="text-muted d-block mb-2">
                                                From name: <strong>{senderDisplayName(action.receipt)}</strong>.
                                                The sender chooses this name, so check it before you match on it.
                                            </small>
                                        )}
                                        <MemberSearchField onSelect={setAssignMember} disabled={actionInProgress} />
                                        <small className="text-muted d-block">
                                            This adds the receipt to the review queue for this member and emails them that it
                                            was received. No points are booked until it&apos;s approved there.
                                        </small>
                                    </>
                                ) : (
                                    <div className="form-group">
                                        <label htmlFor="unclaimed-discard-reason">
                                            Reason<span className="f_red">*</span>
                                        </label>
                                        <textarea
                                            id="unclaimed-discard-reason"
                                            className="form-control"
                                            rows={3}
                                            value={discardReason}
                                            onChange={(e) => setDiscardReason(e.target.value)}
                                            placeholder="e.g. Not a receipt, spam, test message"
                                            disabled={actionInProgress}
                                        />
                                        <small className="text-muted d-block mt-1">
                                            A discarded receipt can no longer be claimed. The file and record are kept for audit.
                                        </small>
                                    </div>
                                )}
                                {actionError && <div className="text-danger mt-2">{actionError}</div>}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeAction} disabled={actionInProgress}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={`btn ${action.mode === 'assign' ? 'btn-primary' : 'btn-danger'}`}
                                    style={action.mode === 'assign' ? { backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' } : undefined}
                                    onClick={submitAction}
                                    disabled={!canSubmitAction}
                                >
                                    {actionInProgress
                                        ? 'Saving…'
                                        : action.mode === 'assign' ? 'Assign & submit for review' : 'Discard receipt'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default UnclaimedLoyaltyReceipts;
